import { motion, useAnimate } from "framer-motion";

import { ArrowLeft } from "..";
import { AspectRatio } from "../ui/aspect-ratio";
import { AspectRatios } from "~/utils/constants";
import { Button } from "../ui/button";
import Image from "../Image";
import { Link } from "@remix-run/react";
import { useEffect } from "react";

export const ThreeColGrid = ({
  hasImage,
  locale,
}: {
  hasImage?: boolean;
  locale?: string;
}) => {
  const text = "ELEGANT. TIMELESS. EXCLUSIVE.";
  const textSE = "ELEGANT. TIDSLÖS. EXKLUSIV.";
  const characters = locale === "se" ? textSE.split("") : text.split("");

  const radius = 140;

  const letterSpacing = 12;

  const [scope, animate] = useAnimate();

  useEffect(() => {
    const animateLoader = async () => {
      const letterAnimation: any = [];

      characters.forEach((_, i) => {
        letterAnimation.push([
          `.letter-${i}`,
          { opacity: 1 },
          { duration: 1.0, at: i === 0 ? "+0.8" : "-0.28" },
        ]);
      });
      animate(letterAnimation, {
        ease: "linear",
        repeat: Infinity,
      });
      animate(
        scope.current,
        { rotate: 360 },
        { duration: 16, ease: "linear", repeat: Infinity }
      );
    };

    if (scope.current) {
      animateLoader();
    }
  }, [scope]);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2" key={"123"}>
        <div className="col-span-4 md:col-span-1">
          <Link to={`/${locale}/new-arrivals`}>
            <div className="relative w-full h-0 pb-[125%]">
              <div className="absolute top-0 left-0 w-full h-full">
                <AspectRatio ratio={AspectRatios.tall}>
                  <Image
                    imageSrcUrl="https://cdn.sanity.io/images/n8c7yri3/production/517b175f290e0259fa610dd629037f08b955442d-1024x1280.jpg"
                    alt="White rose petals"
                  />
                </AspectRatio>
              </div>
            </div>
          </Link>
          <Button asChild variant="link" className="m-[0.25rem]">
            <Link className="underline" to={`/${locale}/new-arrivals`}>
              {locale === "se" ? "NYA POSTERS" : "NEW POSTERS"}
              <ArrowLeft rotate />
            </Link>
          </Button>
        </div>

        <div className="col-span-4 md:col-span-1" key={"234"}>
          <Link to={`/${locale}/abstract`}>
            <div className="relative w-full h-0 pb-[125%]">
              <div className="absolute top-0 left-0 w-full h-full">
                <AspectRatio ratio={AspectRatios.tall}>
                  <Image
                    imageSrcUrl="https://cdn.sanity.io/images/n8c7yri3/production/64519b7cec0c146cd8c4d2269acd3a1c9f9c2dbb-1024x1280.png"
                    alt="Abstract black lines on grey background"
                  />
                </AspectRatio>
              </div>
            </div>
          </Link>
          <Button asChild variant="link" className="m-[0.25rem]">
            <Link className="underline" to={`/${locale}/abstract`}>
              {locale === "se" ? "ABSTRAKT" : "ABSTRACT"}

              <ArrowLeft rotate />
            </Link>
          </Button>
        </div>

        {hasImage ? (
          <div className="col-span-4 md:col-span-1" key={"345"}>
            <Link to={`/${locale}/animal`}>
              <div className="relative w-full h-0 pb-[125%]">
                <div className="absolute top-0 left-0 w-full h-full">
                  <AspectRatio ratio={AspectRatios.tall}>
                    <Image
                      imageSrcUrl="https://cdn.sanity.io/images/n8c7yri3/production/2d49898e9aa0db1a7bbd115625b0858c90ccfd38-1024x1280.jpg"
                      alt="A focused panther in a black background"
                    />
                  </AspectRatio>
                </div>
              </div>
            </Link>
            <Button asChild variant="link" className="m-[0.25rem]">
              <Link className="underline" to={`/${locale}/animal`}>
                {locale === "se" ? "DJUR" : "ANIMAL"}
                <ArrowLeft rotate />
              </Link>
            </Button>
          </div>
        ) : (
          <div
            className="my-auto mx-12 md:mx-auto  md:ml-[-1rem] lg:my-auto  md:mr-0 lg:mx-auto pt-8 lg:pt-0  md:overflow-hidden"
            key={"456"}
          >
            <motion.div
              ref={scope}
              className="relative aspect-square"
              style={{ width: radius * 2 }}
            >
              <p aria-label={text} />
              <p aria-hidden="true" className="text">
                {characters.map((ch, i) => (
                  <motion.span
                    key={i}
                    className={`absolute top-0 left-1/2 font-rubik-mono-one text-black letter-${i} text-3xl `}
                    style={{
                      transformOrigin: `0 ${radius}px`,
                      transform: `rotate(${i * letterSpacing}deg)`,
                    }}
                  >
                    {ch}
                  </motion.span>
                ))}
              </p>
            </motion.div>
          </div>
        )}
      </div>
    </>
  );
};

export default ThreeColGrid;
